import { WorkflowRunStep, WorkflowRunStepState } from '@digital-twins/shared-types'

import { ThreadArtifactItem } from './collectThreadArtifacts'

export interface ThreadArtifactStepGroup {
  /** `sourceStepId`, or `''` for the trailing group of unattributed items. */
  key: string
  /** The producing step's H2 heading. Null when no step matches. */
  title: string | null
  state: WorkflowRunStepState | null
  /** Step clock for the relative-time label: `completedAt`, else `startedAt`. */
  at: string | null
  items: Array<ThreadArtifactItem>
}

/**
 * Groups a run's artifacts under the step that produced them, in run order.
 *
 * Attribution is `ThreadArtifactItem.sourceStepId` (Step 3), so this stays
 * generic over any template: it never reads a step key, a title or an
 * artifact kind to decide where a card belongs.
 *
 * Ordering is deliberately the reverse of the wire. The API hands artifacts
 * newest-first, which reads as a run played backwards; grouping by
 * `stepIndex` ascending makes the column read the way the run actually ran,
 * which is the point of the step grouping. Within a group, `position` orders
 * a fan-out set (0 = A) and falls back to the incoming order.
 *
 * Items with no `sourceStepId`, or whose step is not in `steps` (living
 * artifacts, a legacy run), collect in one trailing untitled group so nothing
 * is dropped. With no steps at all the result is a single untitled group
 * holding everything in arrival order, which is the pre-grouping behaviour.
 */
export function groupArtifactsByStep(
  items: Array<ThreadArtifactItem>,
  steps: Array<WorkflowRunStep>,
): Array<ThreadArtifactStepGroup> {
  if (items.length === 0) {
    return []
  }

  const stepById = new Map<string, WorkflowRunStep>()
  steps.forEach((step) => {
    stepById.set(step.stepId, step)
  })

  const grouped = new Map<string, Array<ThreadArtifactItem>>()
  const ungrouped: Array<ThreadArtifactItem> = []

  items.forEach((item) => {
    const stepId = item.sourceStepId ?? ''
    if (!stepId || !stepById.has(stepId)) {
      ungrouped.push(item)
      return
    }
    const bucket = grouped.get(stepId)
    if (bucket) {
      bucket.push(item)
      return
    }
    grouped.set(stepId, [item])
  })

  const orderedStepIds = Array.from(grouped.keys()).sort((left, right) => {
    const leftIndex = stepById.get(left)?.stepIndex ?? 0
    const rightIndex = stepById.get(right)?.stepIndex ?? 0
    return leftIndex - rightIndex
  })

  const groups: Array<ThreadArtifactStepGroup> = orderedStepIds.map((stepId) => {
    const step = stepById.get(stepId) as WorkflowRunStep
    return {
      key: stepId,
      title: step.title,
      state: step.state,
      at: step.completedAt ?? step.startedAt ?? null,
      items: orderWithinGroup(grouped.get(stepId) ?? []),
    }
  })

  if (ungrouped.length > 0) {
    groups.push({
      key: '',
      title: null,
      state: null,
      at: null,
      items: ungrouped,
    })
  }

  return groups
}

/**
 * Fan-out order within one step. `position` is set only when a step produced
 * more than one artifact, so a single-artifact step keeps its incoming order.
 */
function orderWithinGroup(
  items: Array<ThreadArtifactItem>,
): Array<ThreadArtifactItem> {
  const hasPositions = items.some((item) => item.position != null)
  if (!hasPositions) {
    return items
  }
  return [...items].sort((left, right) => {
    return (left.position ?? 0) - (right.position ?? 0)
  })
}
